import {
  Dropdown,
  Menu,
  Button,
  message,
  Spin,
  Modal,
  Tooltip,
  Switch,
  Tag,
  Typography,
  Alert,
  Input,
} from "antd"
import {
  CloseOutlined,
  CloudSyncOutlined,
  CopyOutlined,
  DeleteOutlined,
  MoreOutlined,
  SyncOutlined,
  ToolOutlined,
} from "@ant-design/icons"
import { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import apiClient from "../../Utils/apiClient"
import PanelLayout from "../../Layout/PanelLayout"
import TableBox from "../../components/TableBox/TableBox"
import HostnameLink from "../../components/HostnameLink/HostnameLink"
import { useNotification } from "../../components/Notification/Notification"
import {
  getStoredPageSize,
  getInventoryViews,
  saveInventoryView,
  deleteInventoryView,
} from "../../Utils/TableUtils/TableUtils"
import dayjs from "dayjs"
import { useHeaderContext } from "../../Utils/HeaderContext"
import { formatAgentVersion } from "../../Utils/utils"
import { formatIdle } from "../../Utils/userActivity"
import {
  DEVICE_CATEGORY_COLORS,
  deviceCategoryLabelOf,
  deviceCategoryTitle,
} from "../../Utils/deviceCategory"
import AutoCompleteBox from "../../components/AutoCompleteBox/AutoCompleteBox"
import DeviceModelImage from "../../components/DeviceModelImage/DeviceModelImage"
import { resolveDeviceModelImage } from "../../Utils/deviceModelImage"
import StatusTag from "../../components/StatusTag/StatusTag"
import { useSelector } from "react-redux"

const shortUser = (value) => {
  const text = String(value || "").trim()
  if (!text) return "Unassigned"
  const parts = text.split(/\\|\//)
  return parts[parts.length - 1] || text
}

const { Text } = Typography

const DEVICE_LIST_PROJECTIONS = JSON.stringify([
  "hostname",
  "serialNumber",
  "user",
  "agentversion",
  "deviceStatus",
  "usbStatus",
  "complianceStatus",
  "sysInfo",
  "pendingReboot",
  "pendingRebootReason",
  "lastReportReceivedTime",
  "commsLastSeenAt",
  "agentHeartbeatAt",
  "osPlatform",
  "osName",
  "cpu",
  "ram",
  "idle_seconds",
  "currentActivity",
  "lastUserActivityAt",
  "groupedIn",
  "reboot",
])

const copyText = async (value, successText = "Copied") => {
  try {
    await navigator.clipboard.writeText(value)
    message.success(successText)
    return true
  } catch {
    window.prompt("Copy this command", value)
    return false
  }
}

const Devices = ({
  preset = {},
  locked = [],
  title: titleOverride,
} = {}) => {
  const page = "All Devices"
  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState({
    hostname: searchParams.get("hostname") || "",
    user: "",
    model: "",
    usbStatus: "",
  })

  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: getStoredPageSize(page) || 10,
    total: 0,
  })

  const userInfo = useSelector((state) => state.user.data)
  const isAdmin = userInfo?.role === "network-admin" || false

  const [loading, setLoading] = useState(true)
  const [devices, setDevices] = useState([])
  const [filteredDevices, setFilteredDevices] = useState([])
  const [exportData, setExportData] = useState([])
  const [actionState, setActionState] = useState({})
  const [isDisabledSync, setIsDisabledSync] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const navigate = useNavigate()
  const [savedViews, setSavedViews] = useState(getInventoryViews)
  const statusFilter = locked.includes("status")
    ? preset.status || ""
    : searchParams.get("status") || preset.status || ""
  const usbFilter = locked.includes("usb")
    ? preset.usb || ""
    : searchParams.get("usb") || preset.usb || ""
  const complianceFilter = locked.includes("compliance")
    ? preset.compliance || ""
    : searchParams.get("compliance") || preset.compliance || ""
  const categoryFilter = locked.includes("category")
    ? preset.category || ""
    : searchParams.get("category") || preset.category || ""
  const collectionFilter = locked.includes("collection")
    ? preset.collection || ""
    : searchParams.get("collection") || preset.collection || ""
  const agentKindFilter = searchParams.get("agentKind") || ""
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [deviceToDelete, setDeviceToDelete] = useState(null)
  const [singleDelete, setSingleDelete] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [agentRelease, setAgentRelease] = useState("")
  const [updatingAgents, setUpdatingAgents] = useState(false)
  const [repairShare, setRepairShare] = useState(null)
  const notify = useNotification()
  const {
    updateDeviceTask,
    removeDeviceTask,
    setTaskId,
    setRender,
    isDeviceSyncing,
    deviceTasks,
  } = useHeaderContext()

  const isAnyDeviceSyncing = () => {
    return Object.values(deviceTasks).some(
      (task) =>
        task.command === "sync" && ["pending", "running"].includes(task.status)
    )
  }

  const [usbStatus, setUsbStatus] = useState(
    filteredDevices.usbStatus || "Unknown"
  )
  const [switchUsbLoading, setSwitchUsbLoading] = useState(false)

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
  }

  const showDeleteConfirm = (id, record) => {
    setDeviceToDelete(id)
    setSingleDelete(record)
    setDeleteModalVisible(true)
  }

  const handleSyncAll = async () => {
    try {
      setIsDisabledSync(true)

      // enable after 30 sec
      setTimeout(() => {
        setIsDisabledSync(false)
      }, 10000)
      const res = await apiClient("devices/syncAll", "POST")

      if (res.success) {
        message.success(res.message)
      }
    } catch (err) {
      message.error(err.message || `Failed to sync all devices`)
    }
  }

  const handleSync = async () => {
    try {
      setIsDisabledSync(true)
      setTimeout(() => {
        setIsDisabledSync(false)
      }, 10000)
      const payload = {
        ids: selectedRowKeys,
        command: "sync",
      }

      const res = await apiClient("devices/tasks", "POST", payload)

      if (res.success) {
        message.success(res.message)
      }
    } catch (err) {
      message.error(err.message || `Failed to sync selected devices`)
    }
  }

  const queueAgentUpdates = async ({ ids, hostname }) => {
    setUpdatingAgents(true)
    try {
      const res = hostname
        ? await apiClient(`devices/${hostname}/update-agent`, "POST")
        : await apiClient("devices/update-agents", "POST", { ids })
      if (res?.success === false) {
        throw new Error(res.message || "Failed to queue agent update")
      }
      if (res) {
        message.success(res.message || "Agent update queued")
      }
    } catch (err) {
      message.error(err.message || "Failed to queue agent update")
    } finally {
      setUpdatingAgents(false)
    }
  }

  const handleUpdateAgent = (record) => {
    const current = formatAgentVersion(record?.agentversion) || "unknown"
    const target = formatAgentVersion(agentRelease) || "the current agent"
    Modal.confirm({
      title: "Update Xpanse agent?",
      content: /^1\./.test(String(record?.agentversion || ""))
        ? `This PC is on the Python agent (${current}). Update agent will install ${target} and then stop the old Python agent. If the PC is offline, it will run when the current agent next checks in.`
        : `Install ${target} on ${record?.hostname}. Current version: ${current}. Queue it from here; if the PC is offline, reboot it so the agent can pick the job up.`,
      okText: "Update agent",
      onOk: () => queueAgentUpdates({ hostname: record?.hostname }),
    })
  }

  const handleUpdateSelectedAgents = () => {
    const target = formatAgentVersion(agentRelease) || "the current agent"
    Modal.confirm({
      title: "Update Xpanse agent?",
      content: `Install ${target} on selected devices that are not already current. PCs already on ${target} are skipped.`,
      okText: "Update agent",
      onOk: () => queueAgentUpdates({ ids: selectedRowKeys }),
    })
  }

  const handleRepairAgent = (record) => {
    Modal.confirm({
      title: `Repair agent on ${record?.hostname}?`,
      content:
        "Use this when the agent shows Invalid communication agent key, never checks in, or Xpanse is missing on the PC. This issues a new PowerShell install command and invalidates the old key. Run the new command as Administrator on that PC immediately, wait about 30 seconds, then Update agent.",
      okText: "Get repair command",
      okType: "danger",
      onOk: async () => {
        const res = await apiClient(
          `devices/${encodeURIComponent(record?.hostname)}/repair-agent`,
          "POST"
        )
        if (res?.success === false) {
          message.error(res.message || "Could not issue repair command")
          return
        }
        message.success(res?.message || "Repair command ready")
        const cmd =
          res?.result?.commsInstallCommand ||
          res?.result?.bootstrapCommand ||
          ""
        if (cmd) {
          setRepairShare({
            hostname: record?.hostname,
            bootstrapCommand: cmd,
          })
          copyText(cmd, "Repair command copied")
        }
      },
    })
  }

  const handleToggleUSB = async (record) => {
    const key = record._id || record.hostname
    setSwitchUsbLoading((prev) => ({ ...prev, [key]: true }))
    try {
      const newStatus = record?.usbStatus === "Enabled" ? "Disabled" : "Enabled"
      const command =
        record?.usbStatus === "Enabled" ? "usb-disable" : "usb-enable"
      const payload = { command, hostname: record?.hostname }

      message.info(
        `USB ${newStatus} for device: ${record?.hostname} hs started`
      )
      const response = await apiClient(
        `devices/${record?.hostname}/task`,
        "POST",
        payload
      )

      if (response.success) {
        notify({
          title: response.title,
          type: response.type,
          description: (
            <>
              <div>Status : {response.status}</div>
              <div>Message : {response.message}</div>
            </>
          ),
        })
        setUsbStatus(newStatus)
      } else {
        notify({
          title: response.title,
          type: response.type,
          description: (
            <>
              <div>Status : {response.status}</div>
              <div>Message : {response.message}</div>
            </>
          ),
        })
      }
    } catch (error) {
      message.error(
        error.message || "Something went wrong while updating USB status"
      )
    } finally {
      setSwitchUsbLoading((prev) => ({ ...prev, [key]: false }))
    }
  }

  const handleConfirmDelete = () => {
    if (deviceToDelete) {
      handleBulkDeleteDevices(deviceToDelete)
      setDeleteModalVisible(false)
      setDeviceToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setDeleteModalVisible(false)
    setDeviceToDelete(null)
  }

  const fetchDeviceData = async (
    customPage = paginationData.page,
    customPageSize = paginationData.pageSize
  ) => {
    setLoading(true)
    try {
      const params = {
        page: customPage,
        pageSize: customPageSize,
        projections: DEVICE_LIST_PROJECTIONS,
      }
      if (statusFilter) params.deviceStatus = statusFilter
      if (usbFilter) params.usbStatus = usbFilter
      if (complianceFilter) params.complianceStatus = complianceFilter
      if (categoryFilter) params.category = categoryFilter
      if (collectionFilter) params.collection = collectionFilter
      if (agentKindFilter) params.agentKind = agentKindFilter
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value
      })

      const res = await apiClient("devices", "GET", null, { params })

      if (res?.data) {
        setDevices(res.data)
        setFilteredDevices(res.data)
        setPaginationData(res.pageData)
      }
    } catch (err) {
      console.log("Error fetching devices", err)
    } finally {
      setLoading(false)
    }
  }

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination
    setPaginationData((prev) => ({ ...prev, page: current, pageSize }))

    const hasFilters = Object.values(filters).some((val) => val.trim() !== "")

    if (hasFilters) {
      handleFilter(current, pageSize)
    } else {
      fetchDeviceData(current, pageSize)
    }
  }

  useEffect(() => {
    fetchDeviceData(1, paginationData.pageSize)
  }, [statusFilter, usbFilter, complianceFilter, categoryFilter, collectionFilter, agentKindFilter, filters.hostname])

  useEffect(() => {
    let cancelled = false
    apiClient("devices/agent-release")
      .then((res) => {
        if (!cancelled) setAgentRelease(res?.version || "")
      })
      .catch(() => {
        if (!cancelled) setAgentRelease("")
      })
    return () => {
      cancelled = true
    }
  }, [])

  const updateDeviceInState = (updated) => {
    setDevices((prev) =>
      prev.map((d) => (d.hostname === updated.hostname ? updated : d))
    )
  }

  const handleDeviceClick = (device) => {
    navigate(`/device-management/${device._id}`)
  }

  const handleFilter = async (
    customPage = paginationData.page,
    customPageSize = paginationData.pageSize
  ) => {
    setLoading(true)
    try {
      const params = {
        page: customPage,
        pageSize: customPageSize,
        projections: DEVICE_LIST_PROJECTIONS,
        ...filters,
      }
      if (statusFilter) params.deviceStatus = statusFilter
      if (usbFilter) params.usbStatus = usbFilter
      if (complianceFilter) params.complianceStatus = complianceFilter
      if (categoryFilter) params.category = categoryFilter
      if (collectionFilter) params.collection = collectionFilter
      if (agentKindFilter) params.agentKind = agentKindFilter
      const res = await apiClient("devices", "GET", null, { params })

      if (res?.data) {
        setFilteredDevices(res.data)
        setPaginationData(res.pageData)
      }
    } catch (err) {
      console.error("Filter failed", err)
    } finally {
      setLoading(false)
      setDrawerOpen(false)
    }
  }

  const handleClearFilter = () => {
    setFilters({
      hostname: "",
      user: "",
      model: "",
      usbStatus: "",
    })
    fetchDeviceData(1, paginationData.pageSize)
    setDrawerOpen(false)
  }

  const handleBulkDeleteDevices = async (ids) => {
    try {
      const res = await apiClient("devices/delete", "POST", { ids })
      if (res.success) {
        message.success(res.message || "Devices deleted successfully")
        setDevices((prev) => prev.filter((d) => !ids.includes(d._id)))
        setFilteredDevices((prev) => prev.filter((d) => !ids.includes(d._id)))
        setSelectedRowKeys([])
      } else {
        message.error(res.message || "Failed to delete devices")
      }
    } catch (err) {
      console.error("Bulk delete failed", err)
      message.error("Failed to delete devices")
    } finally {
      setDeleteModalVisible(false)
      setDeviceToDelete(null)
      fetchDeviceData(paginationData.page, paginationData.pageSize)
    }
  }
  const baseColumns = [
    {
      title: "Device",
      dataIndex: "hostname",
      key: "hostname",
      width: 280,
      fixed: "left",
      render: (text, record) => {
        const model = resolveDeviceModelImage(record)
        const isSyncing = isDeviceSyncing(record.hostname)
        return (
          <div className="inv-device" style={{ opacity: isSyncing ? 0.55 : 1 }}>
            <DeviceModelImage device={record} size="sm" />
            <div className="inv-device__copy">
              <div className="inv-device__title">
                <span
                  className={`inv-device__pulse inv-device__pulse--${String(
                    record.deviceStatus || "offline"
                  )
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  aria-hidden
                />
                <HostnameLink record={record} isAdmin={isAdmin}>
                  {text}
                </HostnameLink>
                {isSyncing ? <Spin size="small" /> : null}
              </div>
              <div className="inv-device__meta" title={record.user || ""}>
                <span>{shortUser(record.user)}</span>
                <span className="inv-device__dot" />
                <span title={model.displayModel}>{model.label}</span>
              </div>
            </div>
          </div>
        )
      },
    },
    {
      title: "Status",
      dataIndex: "deviceStatus",
      key: "deviceStatus",
      width: 108,
      render: (status) => <StatusTag status={status} />,
    },
    {
      title: "Agent",
      dataIndex: "agentversion",
      key: "agentversion",
      width: 110,
      render: (value, record) => (
        <>
          <span className="inv-mono">{formatAgentVersion(value) || "—"}</span>
          {record.pendingReboot ? (
            <Tag color="orange" style={{ marginLeft: 6 }}>
              Reboot
            </Tag>
          ) : null}
        </>
      ),
    },
    {
      title: "Type",
      dataIndex: "deviceCategory",
      key: "deviceCategory",
      width: 120,
      render: (_, record) => {
        const label = deviceCategoryLabelOf(record)
        if (!label) return "—"
        return (
          <Tag
            className="device-type-tag"
            color={DEVICE_CATEGORY_COLORS[record.deviceCategory] || "default"}
          >
            {label.replace(/^Windows\s+/i, "")}
          </Tag>
        )
      },
    },
    {
      title: "Idle",
      dataIndex: "idleSeconds",
      key: "idleSeconds",
      width: 96,
      render: (seconds, record) => (
        <span className="inv-idle">
          {formatIdle(seconds ?? record.idle_seconds)}
        </span>
      ),
    },
    {
      title: "Model",
      dataIndex: ["sysInfo", "Model"],
      key: "model",
      width: 160,
      ellipsis: true,
      render: (_, record) => {
        const model = resolveDeviceModelImage(record)
        return (
          <span className="inv-mono" title={model.displayModel}>
            {model.displayModel || "—"}
          </span>
        )
      },
    },
    {
      title: "System Type",
      dataIndex: ["sysInfo", "System Type"],
      key: "systemType",
      width: 140,
      ellipsis: true,
      render: (value) => value || "—",
    },
    {
      title: "Total Memory (GB)",
      width: 160,
      dataIndex: ["sysInfo", "Total Physical Memory (GB)"],
      key: "memory",
      render: (value) => value || "—",
    },
    {
      title: "Serial Number",
      dataIndex: ["sysInfo", "SerialNumber"],
      key: "serial",
      width: 140,
      ellipsis: true,
      render: (value) => value || "—",
    },
    {
      title: "Total Disk (GB)",
      width: 128,
      key: "diskSize",
      ellipsis: true,
      render: (_, record) => {
        const disks = record?.sysInfo?.Disks || []
        const totalSize = disks.reduce(
          (sum, disk) => sum + (parseFloat(disk["Size (GB)"]) || 0),
          0
        )
        return totalSize ? totalSize.toFixed(2) : "—"
      },
    },
  ]

  const columns =
    isAdmin && selectedRowKeys.length === 0
      ? [
          ...baseColumns,
          {
            title: "Actions",
            key: "actions",
            width: 88,
            fixed: "right",
            align: "center",
            render: (text, record) => (
              <Dropdown
                trigger={["click"]}
                arrow
                placement="bottomRight"
                overlay={
                  <Menu>
                    <Menu.Item
                      key="update-agent"
                      onClick={() => handleUpdateAgent(record)}
                    >
                      <CloudSyncOutlined /> Update agent
                    </Menu.Item>
                    <Menu.Item
                      key="repair-agent"
                      onClick={() => handleRepairAgent(record)}
                    >
                      <ToolOutlined /> Repair agent
                    </Menu.Item>
                    <Menu.Item
                      key="delete"
                      onClick={() => showDeleteConfirm([record?._id], record)}
                    >
                      <DeleteOutlined style={{ color: "red" }} /> Delete Device
                    </Menu.Item>
                    <Menu.Item key="usb-toggle">
                      {switchUsbLoading[record._id || record.hostname] ? (
                        <>
                          <Spin />
                          <span>
                            {record?.usbStatus === "Enabled"
                              ? "Disabling..."
                              : "Enabling..."}
                          </span>
                        </>
                      ) : (
                        <Tooltip title-="this will disable usb">
                          <Switch
                            checked={record?.usbStatus === "Enabled"}
                            disabled={
                              switchUsbLoading[record?._id || record?.hostname]
                            }
                            onChange={() => handleToggleUSB(record)}
                          />
                          <span className="px-2">
                            {record?.usbStatus === "Enabled"
                              ? "Disable USB?"
                              : "Enable USB?"}
                          </span>
                        </Tooltip>
                      )}
                    </Menu.Item>
                  </Menu>
                }
              >
                <Button size="small" icon={<MoreOutlined />} />
              </Dropdown>
            ),
          },
        ]
      : baseColumns

  // ...existing code...

  const exportColumns = columns
    .filter((col) => !["actions"].includes(col.key))
    .map(({ title, dataIndex }) => ({ title, dataIndex }))

  // const formatExportData = (data) => {
  //   return data?.map((device) => ({
  //     hostname: device?.hostname,
  //     user: device?.user,
  //     reboot: device?.reboot,
  //     manufacturer: device?.sysInfo.Manufacturer,
  //     model: device?.sysInfo.Model,
  //     systemType: device?.sysInfo["System Type"],
  //     totalMemory: device?.sysInfo["Total Physical Memory (GB)"],
  //     totalDiskSize: device?.sysInfo["Total Physical Memory (GB)"],
  //     serialNumber: device?.sysInfo.SerialNumber,
  //     usbStatus: device?.usbStatus,
  //   }))
  // }
  const formatExportData = (data) => {
    return data?.map((device) => {
      const disks = device?.sysInfo?.Disks || []
      const totalDiskSize = disks.reduce(
        (sum, disk) => sum + (parseFloat(disk["Size (GB)"]) || 0),
        0
      )

      return {
        hostname: device?.hostname,
        user: device?.user,
        reboot: device?.reboot,
        manufacturer: device?.sysInfo?.Manufacturer,
        model: device?.sysInfo?.Model,
        systemType: device?.sysInfo?.["System Type"],
        totalMemory: device?.sysInfo?.["Total Physical Memory (GB)"],
        totalDiskSize: totalDiskSize.toFixed(2),
        serialNumber: device?.sysInfo?.SerialNumber,
      }
    })
  }

  const exportOptions = {
    endpoint: "filter/users",
    method: "POST",
    params: {
      ...filters,
    },
    onExportData: (data) => setExportData(data),
    format: formatExportData,
    data: formatExportData(exportData),
    header: [
      "Hostname",
      "User",
      "Last Reboot",
      "Manufacturer",
      "Model",
      "System Type",
      "Total Memory (GB)",
      "Total Disk Size (GB)",
      "Serial Number",
    ],
    btnTitle: "Export",
    fileName: "Asset Inventory",
    pdfWithout: ["systemType", "reboot"],
  }

  const renderTableTitle = () => {
    return (
      <div className="d-flex justify-content-between align-items-center">
        <Text style={{ fontSize: "15px" }}> All Devices</Text>
        {isAdmin && (
          <div className="d-flex flex-row-reverse justify-content-between align-items-center gap-2">
            {selectedRowKeys.length === 0 ? (
              <Tooltip
                title={
                  isAnyDeviceSyncing()
                    ? "Sync operation is in progress"
                    : filteredDevices?.length === 0
                    ? "No devices available"
                    : ""
                }
                placement="top"
                open={
                  filteredDevices?.length === 0 || isAnyDeviceSyncing()
                    ? undefined
                    : false
                }
              >
                <Button
                  onClick={handleSyncAll}
                  // disabled={isAnyDeviceSyncing()}
                  disabled={isDisabledSync}
                  // loading={isAnyDeviceSyncing()}
                  // size="small"
                  type="primary"
                  icon={<SyncOutlined />}
                >
                  {!isAnyDeviceSyncing() ? "Sync All Devices" : "Syncing ..."}
                </Button>
              </Tooltip>
            ) : (
              <Button
                onClick={handleSync}
                // disabled={isAnyDeviceSyncing()}
                disabled={isDisabledSync}
                // loading={isAnyDeviceSyncing()}
                size="small"
                type="primary"
                icon={<SyncOutlined />}
              >
                {!isAnyDeviceSyncing()
                  ? `Sync (${selectedRowKeys?.length}) Devices`
                  : "Syncing ..."}
              </Button>
            )}
            {selectedRowKeys.length > 0 ? (
              <Button
                icon={<CloudSyncOutlined />}
                loading={updatingAgents}
                onClick={handleUpdateSelectedAgents}
              >
                Update agent ({selectedRowKeys.length})
              </Button>
            ) : null}
            {
              selectedRowKeys.length > 0 && (
                <Button
                  type="primary"
                  danger
                  size="small"
                  onClick={() => {
                    Modal.confirm({
                      title: "Confirm Delete",
                      content: `Are you sure you want to delete ${selectedRowKeys.length} selected devices?`,
                      okText: "Delete",
                      okType: "danger",
                      onOk: () => handleBulkDeleteDevices(selectedRowKeys),
                    })
                  }}
                >
                  Delete ({selectedRowKeys.length})
                  <CloseOutlined
                    style={{ marginLeft: 8 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedRowKeys([])
                    }}
                    title="Clear Selection"
                  />
                </Button>
              )
              // : (
              //   <Button
              //     type="primary"
              //     size="small"
              //     icon={<DeleteOutlined />}
              //     onClick={() => navigate("/deleted-devices")}
              //   >
              //     Deleted Devices
              //   </Button>
              // )
            }
          </div>
        )}
      </div>
    )
  }

  const setInventoryFilter = (key, value) => {
    if (locked.includes(key)) return
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next)
  }

  const handleSaveView = () => {
    const name = window.prompt("Name this view")
    if (!name) return
    setSavedViews(
      saveInventoryView({
        name,
        status: statusFilter,
        usb: usbFilter,
        compliance: complianceFilter,
      })
    )
  }

  const applyView = (view) => {
    const next = new URLSearchParams()
    if (view.status) next.set("status", view.status)
    if (view.usb) next.set("usb", view.usb)
    if (view.compliance) next.set("compliance", view.compliance)
    setSearchParams(next)
  }

  return (
    <PanelLayout
      title={titleOverride || deviceCategoryTitle(categoryFilter) || "Device inventory"}
      subtitle="Model, presence, and agent posture — sync, filter, and open a device for full control."
    >
      {/* <div className="mb-3 d-flex gap-2 justify-content-end align-items-center">
        {selectedRowKeys.length === 0 ? (
          <Tooltip
            title={
              isAnyDeviceSyncing()
                ? "Sync operation is in progress"
                : filteredDevices?.length === 0
                ? "No devices available"
                : ""
            }
            placement="top"
            open={
              filteredDevices?.length === 0 || isAnyDeviceSyncing()
                ? undefined
                : false
            }
          >
            <Button
              onClick={handleSyncAll}
              // disabled={isAnyDeviceSyncing()}
              disabled={isDisabledSync}
              // loading={isAnyDeviceSyncing()}
              size="small"
              type="primary"
              icon={<SyncOutlined />}
            >
              {!isAnyDeviceSyncing() ? "Sync All Devices" : "Syncing ..."}
            </Button>
          </Tooltip>
        ) : (
          <Button
            onClick={handleSync}
            // disabled={isAnyDeviceSyncing()}
            disabled={isDisabledSync}
            // loading={isAnyDeviceSyncing()}
            size="small"
            type="primary"
            icon={<SyncOutlined />}
          >
            {!isAnyDeviceSyncing()
              ? `Sync (${selectedRowKeys?.length}) Devices`
              : "Syncing ..."}
          </Button>
        )}
        {
          selectedRowKeys.length > 0 && (
            <Button
              type="primary"
              danger
              size="small"
              onClick={() => {
                Modal.confirm({
                  title: "Confirm Delete",
                  content: `Are you sure you want to delete ${selectedRowKeys.length} selected devices?`,
                  okText: "Delete",
                  okType: "danger",
                  onOk: () => handleBulkDeleteDevices(selectedRowKeys),
                });
              }}
            >
              Delete ({selectedRowKeys.length})
              <CloseOutlined
                style={{ marginLeft: 8 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedRowKeys([]);
                }}
                title="Clear Selection"
              />
            </Button>
          )
          // : (
          //   <Button
          //     type="primary"
          //     size="small"
          //     icon={<DeleteOutlined />}
          //     onClick={() => navigate("/deleted-devices")}
          //   >
          //     Deleted Devices
          //   </Button>
          // )
        }
      </div> */}

      {selectedRowKeys.length > 0 && isAdmin ? (
        <div className="inventory-bulk-bar">
          <span>{selectedRowKeys.length} selected</span>
          <Button
            size="small"
            type="primary"
            icon={<SyncOutlined />}
            disabled={isDisabledSync}
            onClick={handleSync}
          >
            Sync selected
          </Button>
          <Button
            size="small"
            danger
            onClick={() =>
              Modal.confirm({
                title: "Confirm delete",
                content: `Delete ${selectedRowKeys.length} selected devices?`,
                okText: "Delete",
                okType: "danger",
                onOk: () => handleBulkDeleteDevices(selectedRowKeys),
              })
            }
          >
            Delete
          </Button>
          <Button size="small" onClick={() => setSelectedRowKeys([])}>
            Clear
          </Button>
        </div>
      ) : null}

      <div className="inventory-toolbar">
        <div className="inventory-toolbar__group">
          <span>Status</span>
          {["", "Online", "Away", "Offline"].map((value) => (
            <Button
              key={value || "all-status"}
              size="small"
              disabled={locked.includes("status")}
              type={statusFilter === value ? "primary" : "default"}
              onClick={() => setInventoryFilter("status", value)}
            >
              {value || "All"}
            </Button>
          ))}
        </div>
        <div className="inventory-toolbar__group">
          <span>USB</span>
          {["", "Enabled", "Disabled"].map((value) => (
            <Button
              key={value || "all-usb"}
              size="small"
              disabled={locked.includes("usb")}
              type={usbFilter === value ? "primary" : "default"}
              onClick={() => setInventoryFilter("usb", value)}
            >
              {value || "All"}
            </Button>
          ))}
        </div>
        <div className="inventory-toolbar__group">
          <span>Compliance</span>
          {["", "Compliant", "Non-Compliant"].map((value) => (
            <Button
              key={value || "all-compliance"}
              size="small"
              disabled={locked.includes("compliance")}
              type={complianceFilter === value ? "primary" : "default"}
              onClick={() => setInventoryFilter("compliance", value)}
            >
              {value || "All"}
            </Button>
          ))}
        </div>
        {locked.length === 0 ? (
          <div className="inventory-toolbar__group">
            <span>Collection</span>
            {[
              ["", "All"],
              ["python-agent", "Python"],
              ["hosted-outdated", "Behind"],
              ["hosted-current", "Current"],
              ["pending-reboot", "Reboot owed"],
              ["stale-agent", "Stale"],
              ["no-cu-40-days", "No CU 40d"],
            ].map(([value, label]) => (
              <Button
                key={value || "all-collection"}
                size="small"
                type={collectionFilter === value ? "primary" : "default"}
                onClick={() => setInventoryFilter("collection", value)}
              >
                {label}
              </Button>
            ))}
          </div>
        ) : null}
        {locked.length === 0 ? (
          <div className="inventory-toolbar__group">
            <span>Views</span>
            <Button size="small" onClick={handleSaveView}>
              Save view
            </Button>
            {savedViews.map((view) => (
              <Button
                key={view.name}
                size="small"
                onClick={() => applyView(view)}
                onContextMenu={(event) => {
                  event.preventDefault()
                  setSavedViews(deleteInventoryView(view.name))
                }}
              >
                {view.name}
              </Button>
            ))}
          </div>
        ) : null}
      </div>

      <TableBox
        exportable
        exportOptions={exportOptions}
        page={page}
        title={renderTableTitle()}
        columns={columns}
        data={filteredDevices}
        loading={loading}
        scroll={{ x: 1600 }}
        className="dashboard-inventory-table devices-table"
        rowSelection={isAdmin ? rowSelection : false}
        pagination={{
          current: paginationData.page,
          pageSize: paginationData.pageSize,
          total: paginationData.total,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        enableFilter
        filtercontent={
          <div className="p-3">
            <AutoCompleteBox
              label={"Hostname"}
              name="hostname"
              className="mb-3 w-100"
              endPoint={`users`}
              placeholder="Search Host Name"
              value={filters.hostname}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, hostname: value }))
              }
            />

            <AutoCompleteBox
              label={"User Name"}
              name="user"
              className="mb-3 w-100"
              endPoint={`users`}
              placeholder="Search User Name"
              value={filters.user}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, user: value }))
              }
            />
            <AutoCompleteBox
              label={"Model"}
              name="model"
              className="mb-3 w-100"
              endPoint={`users`}
              placeholder="Search model"
              value={filters.model}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, model: value }))
              }
            />
            <AutoCompleteBox
              label={"USB Status"}
              name="usbStatus"
              className="mb-3 w-100"
              endPoint={`users`}
              placeholder="Search USB Status"
              value={filters.usbStatus}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, usbStatus: value }))
              }
            />
          </div>
        }
        onFilter={handleFilter}
        onClear={handleClearFilter}
      />

      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete device <b>{singleDelete?.hostname}</b>
          ?
        </p>
      </Modal>

      <Modal
        title={`Repair agent — ${repairShare?.hostname || ""}`}
        open={Boolean(repairShare)}
        onCancel={() => setRepairShare(null)}
        footer={[
          <Button
            key="copy"
            type="primary"
            icon={<CopyOutlined />}
            onClick={() =>
              copyText(repairShare?.bootstrapCommand, "Repair command copied")
            }
          >
            Copy command
          </Button>,
          <Button key="close" onClick={() => setRepairShare(null)}>
            Done
          </Button>,
        ]}
      >
        <Alert
          className="mb-3"
          type="warning"
          showIcon
          message="Run this on the PC now"
          description="This command installs a new communication agent key. The old key stops working immediately. Run it in elevated PowerShell on that device, wait about 30 seconds, then use Update agent."
        />
        <Input.TextArea
          value={repairShare?.bootstrapCommand || ""}
          autoSize={{ minRows: 3, maxRows: 6 }}
          readOnly
        />
      </Modal>
    </PanelLayout>
  )
}

export default Devices
